import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import StatusBadge from "./StatusBadge";

const AuditTable = ({
  logs,
  onSort,
  sortConfig,
  onExport,
  pagination,
  onPageChange,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(logId)) {
      newExpanded?.delete(logId);
    } else {
      newExpanded?.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (field) => {
    onSort(field);
  };

  const getSortIcon = (field) => {
    if (sortConfig?.field !== field) return "ArrowUpDown";
    return sortConfig?.direction === "asc" ? "ArrowUp" : "ArrowDown";
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case "LOGIN":
        return "LogIn";
      case "LOGOUT":
        return "LogOut";
      case "CREATE_INVOICE":
        return "FileText";
      case "UPDATE_INVOICE":
        return "Edit";
      case "DELETE_INVOICE":
        return "Trash2";
      case "SUBMIT_FOR_CLEARANCE":
        return "Send";
      case "GENERATE_PDF":
        return "FileDown";
      case "SETTINGS_CHANGE":
        return "Settings";
      case "USER_CREATE":
        return "UserPlus";
      case "USER_UPDATE":
        return "UserCheck";
      case "EXPORT":
        return "Download";
      default:
        return "Activity";
    }
  };

  const formatActionType = (actionType) => {
    const actionMap = {
      LOGIN: "User Login",
      LOGOUT: "User Logout",
      CREATE_INVOICE: "Invoice Created",
      UPDATE_INVOICE: "Invoice Updated",
      DELETE_INVOICE: "Invoice Deleted",
      SUBMIT_FOR_CLEARANCE: "Submit for Clearance",
      GENERATE_PDF: "Generate PDF",
      SETTINGS_CHANGE: "Settings Changed",
      USER_CREATE: "User Created",
      USER_UPDATE: "User Updated",
      EXPORT: "Data Export",
    };
    return actionMap?.[actionType] || actionType;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort("timestamp")}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Timestamp</span>
                  <Icon name={getSortIcon("timestamp")} size={16} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort("user")}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>User</span>
                  <Icon name={getSortIcon("user")} size={16} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                Action
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                Resource
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                Status
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {logs?.map((log, index) => (
              <React.Fragment key={log?._id}>
                <tr
                  className={`border-b border-border hover:bg-muted/30 transition-colors ${
                    index % 2 === 0 ? "bg-background" : "bg-muted/10"
                  }`}
                >
                  <td className="p-4">
                    <div className="text-sm text-foreground font-mono">
                      {formatTimestamp(log?.createdAt)}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-secondary-foreground">
                          {log?.userId?.name
                            ?.split(" ")
                            ?.map((n) => n?.[0])
                            ?.join("")}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {log?.userId?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {log?.userId?.role}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon
                        name={getActionIcon(log?.action)}
                        size={16}
                        className="text-muted-foreground"
                      />
                      <span className="text-sm text-foreground">
                        {formatActionType(log?.action)}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-foreground">
                      {log?.entityType}
                    </span>
                  </td>
                  <td className="p-4">
                    <StatusBadge status={log?.status} />
                  </td>
                  <td className="p-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleRowExpansion(log?._id)}
                      iconName={
                        expandedRows?.has(log?._id)
                          ? "ChevronUp"
                          : "ChevronDown"
                      }
                      iconPosition="right"
                    >
                      {expandedRows?.has(log?._id) ? "Hide" : "Show"}
                    </Button>
                  </td>
                </tr>
                {expandedRows?.has(log?._id) && (
                  <tr className="bg-muted/20">
                    <td colSpan="7" className="p-6">
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-foreground">
                          Diff Object
                        </h4>
                        {log?.diff ? (
                          <div className="bg-muted/50 p-3 rounded-lg overflow-auto max-h-96">
                            <pre className="text-xs text-foreground whitespace-pre-wrap">
                              {JSON.stringify(log?.diff, null, 2)}
                            </pre>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No diff data available
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Timeline */}
      <div className="lg:hidden">
        {logs?.map((log, index) => (
          <div
            key={log?._id}
            className={`p-4 ${
              index !== logs?.length - 1 ? "border-b border-border" : ""
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon
                    name={getActionIcon(log?.action)}
                    size={16}
                    className="text-primary"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground truncate">
                    {formatActionType(log?.action)}
                  </p>
                  <StatusBadge status={log?.status} size="sm" />
                </div>
                <p className="text-sm text-muted-foreground mb-1">
                  {log?.userId?.name}
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  {formatTimestamp(log?.createdAt)}
                </p>
                <p className="text-sm text-foreground mb-2">
                  {log?.entityType}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleRowExpansion(log?._id)}
                  iconName={
                    expandedRows?.has(log?._id) ? "ChevronUp" : "ChevronDown"
                  }
                  iconPosition="right"
                  className="text-xs"
                >
                  {expandedRows?.has(log?._id)
                    ? "Hide Details"
                    : "Show Details"}
                </Button>
                {expandedRows?.has(log?._id) && (
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg space-y-2">
                    <p className="text-xs text-muted-foreground">
                      User: {log?.userId?.name} ({log?.userId?.role})
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Entity: {log?.entityType}
                    </p>
                    {log?.diff && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Diff:
                        </p>
                        <div className="bg-muted/50 p-2 rounded overflow-auto max-h-48">
                          <pre className="text-xs text-foreground whitespace-pre-wrap">
                            {JSON.stringify(log?.diff, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {logs?.length === 0 && (
        <div className="p-12 text-center">
          <Icon
            name="FileSearch"
            size={48}
            className="text-muted-foreground mx-auto mb-4"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No audit logs found
          </h3>
          <p className="text-muted-foreground">
            Try adjusting your filters or search criteria.
          </p>
        </div>
      )}
      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <div className="p-4 border-t border-border bg-muted/10">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing{" "}
              {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to{" "}
              {Math.min(
                pagination.currentPage * pagination.itemsPerPage,
                pagination.totalItems
              )}{" "}
              of {pagination.totalItems} results
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage || !onPageChange}
                iconName="ChevronLeft"
              >
                Previous
              </Button>
              <span className="text-sm text-foreground px-3 py-1 bg-muted rounded">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage || !onPageChange}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTable;


